<script setup>
const { t } = useI18n();

const props = defineProps({
  slug: String,
  odd: Boolean,
  even: Boolean,
  /** Cal only al primer accordeó de la pàgina: la seva portada és l'LCP. */
  priority: Boolean,
});
const isExpanded = ref(false);
const projects = [
  {
    vithas: {
      title: t("project.items.vithas_title"),
      subtitle: t("project.items.vithas_subtitle"),
      slug: "vithas",
      description: "Av. de Cornellà, 157, Barcelona",
      header: "vithas-portada.jpg",
      items: [
        "vithas-1.jpg",
        "vithas-2.jpg",
        "vithas-3.jpg",
        "vithas-4.jpg",
        "vithas-5.jpg",
        "vithas-6.jpg",
      ],
    },
    edificio_plurifamiliar: {
      title: t("project.items.edificio_plurifamiliar_title"),
      subtitle: t("project.items.edificio_plurifamiliar_subtitle"),
      slug: "edificio-plurifamiliar",
      description: "C. Venezuela, 22@, Barcelona",
      header: "edificio-plurifamiliar-portada.jpg",
      items: [
        "edificio-plurifamiliar-1.jpg",
        "edificio-plurifamiliar-2.jpg",
        "edificio-plurifamiliar-3.jpg",
        "edificio-plurifamiliar-4.jpg",
        "edificio-plurifamiliar-5.jpg",
        "edificio-plurifamiliar-6.jpg",
      ],
    },
    oficines_simon: {
      title: t("project.items.simon_offices_title"),
      subtitle: t("project.items.simon_offices_subtitle"),
      slug: "oficines-simon",
      description: "C. Sancho de Ávila, 66 Barcelona",
      header: "oficines-simon-7.jpg",
      items: [
        "oficines-simon-1.jpg",
        "oficines-simon-2.jpg",
        "oficines-simon-3.jpg",
        "oficines-simon-4.jpg",
        "oficines-simon-5.jpg",
        "oficines-simon-6.jpg",
      ],
    },
    residencia_vita_pedralbes: {
      title: t("project.items.residence_vita_pedralbes_title"),
      subtitle: t("project.items.residence_vita_pedralbes_subtitle"),
      slug: "residencia-vita-pedralbes",
      description:
        "Av. D\’Esplugues, 102, Les Corts, Barcelona",
      header: "residencia-vita-pedralbes-1.jpg",
      items: [
        "residencia-vita-pedralbes-2.jpg",
        "residencia-vita-pedralbes-3.jpg",
        "residencia-vita-pedralbes-4.jpg",
        "residencia-vita-pedralbes-5.jpg",
        "residencia-vita-pedralbes-6.jpg",
        "residencia-vita-pedralbes-7.jpg",
      ],
    },
    oller_del_mas: {
      title: t("project.items.oller_mas_title"),
      subtitle: t("project.items.oller_mas_subtitle"),
      slug: "oller-del-mas",
      description: "Ctra. d\’Igualada C-37z km 91 Manresa",
      header: "oller-del-mas-7.jpg",
      items: [
        "oller-del-mas-1.jpg",
        "oller-del-mas-2.jpg",
        "oller-del-mas-3.jpg",
        "oller-del-mas-4.jpg",
        "oller-del-mas-5.jpg",
        "oller-del-mas-6.jpg",
      ],
    }
  },
];

const project = ref({});
const p = projects.find((p) => p[props.slug]);
if (p) project.value = p[props.slug];

// masonry-wall lays out its columns asynchronously (images, ResizeObserver),
// so the content's real height isn't known yet on the frame `enter` fires.
// We track it live and keep nudging `height` to match while expanded.
let resizeObserver = null;

const enter = (element) => {
  element.style.height = "0px";
  element.style.overflow = "hidden";

  // Wait a couple of frames so masonry-wall has laid out its columns
  // before we measure — otherwise we animate to a stale/short height.
  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (isExpanded.value) element.style.height = `${element.scrollHeight}px`;
      });
    });
  });

  resizeObserver = new ResizeObserver(() => {
    if (isExpanded.value) element.style.height = `${element.scrollHeight}px`;
  });
  resizeObserver.observe(element);
};

const afterEnter = (element) => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  element.style.height = "auto";
};

const leave = (element) => {
  resizeObserver?.disconnect();
  resizeObserver = null;

  element.style.height = `${element.scrollHeight}px`;

  // Force repaint to make sure the
  // animation is triggered correctly.
  getComputedStyle(element).height;

  requestAnimationFrame(() => {
    element.style.height = "0px";
  });
};

const afterLeave = () => {
  resizeObserver?.disconnect();
  resizeObserver = null;
};
</script>

<style lang="css" scoped>
.background-before::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  pointer-events: none;
  /* Per damunt de la portada, que ara és un <img> i no el background del div. */
  z-index: 1;
}

/* CSS for the transition */
.expand-enter-active,
.expand-leave-active {
  transition: height 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  will-change: height;
  backface-visibility: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
}
</style>

<template>
  <div>
    <!-- `isolate` fixa el context d'apilament: la portada (z-0), l'scrim del
         ::before (z-1), el vel blanc d'obertura (z-2) i el text (z-10) es
         pintarien per ordre de DOM si els deixéssim amb z-index auto, i la
         portada taparia l'scrim. -->
    <div
      class="duration-700 transition-[height] background-before w-full h-[500px] lg:h-[865px] relative isolate overflow-hidden"
      :class="{ '!h-[340px]': isExpanded }">
      <AppImage :src="`/projects/${project.slug}/${project.header}`" :alt="project.title ?? ''"
        class="absolute inset-0 z-0" img-class="w-full h-full object-cover" :priority="props.priority"
        sizes="100vw" />
      <div class="bg-transparent duration-700 transition-all h-full absolute w-full opacity-50 z-[2]"
        :class="{ '!bg-white': isExpanded }"></div>
      <div class="mx-auto h-full flex max-w-7xl px-8 lg:px-[60px] py-[110px]" :class="{
        'py-[40px]': isExpanded,
        'items-end lg:justify-end': props.even,
        'items-end lg:justify-start': props.odd,
      }">
        <div class="flex flex-col z-10 relative">
          <div class="flex flex-col border-b pb-4 mb-6"
            :class="{ 'items-start lg:items-end': props.even, 'items-start lg:items-start': props.odd }">
            <div class="text-white font-light uppercase text-5xl lg:text-7xl" v-if="project.title">
              {{ project.title }}
            </div>
            <div class="text-white font-bold uppercase text-5xl lg:text-7xl" v-if="project.subtitle">
              {{ project.subtitle }}
            </div>
          </div>
          <div class="flex" :class="{
            'items-start lg:justify-end': props.even,
            'items-start lg:justify-start': props.odd,
          }">
            <AnchorButton @click="isExpanded = !isExpanded" :label="isExpanded ? $t('project.close') : $t('project.view_project')
              " />
          </div>
        </div>
      </div>
    </div>
    <Transition name="expand" @enter="enter" @after-enter="afterEnter" @leave="leave" @after-leave="afterLeave">
      <div v-show="isExpanded" class="w-full overflow-hidden">
        <div class="px-10 py-10 lg:y-20">
          <!-- <div class="flex flex-col py-8 text-[#515151]">
            <div
              class="font-light uppercase text-6xl"
              v-if="project.title?.length"
            >
              {{ project.title[0] }}
            </div>
            <div
              class="font-bold uppercase text-6xl"
              v-if="project.title?.length === 2"
            >
              {{ project.title[1] }}
            </div>
          </div> -->
          <div class="w-full pb-8 text-2xl">
            {{ project.description }}
          </div>
          <div>
            <masonry-wall :items="project.items" :ssr-columns="1" :column-width="400" :gap="16">
              <template #default="{ item, index }">
                <div :style="{ height: `auto` }">
                  <AppImage :src="`/projects/${project.slug}/${item}`" :alt="project.description"
                    img-class="w-full h-auto" sizes="(min-width: 640px) 400px, 100vw" />
                </div>
              </template>
            </masonry-wall>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
